import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Bell, PackageX, Settings, Check, X, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIST } from "@/lib/time";
import { useAuth } from "@/store/auth";
import { useMatrix } from "@/hooks/usePermission";

// Phase 13D polish -- notification types considered "low stock" for the
// FDE bell pin + red badge treatment.
const LOW_STOCK_TYPES = new Set(["stock_low", "stock_zero", "low_stock"]);
const isLowStockNotif = (n) => {
  if (!n) return false;
  if (LOW_STOCK_TYPES.has(n.type)) return true;
  const tags = Array.isArray(n.tags) ? n.tags : [];
  return tags.includes("low_stock");
};

export function NotificationsBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const refresh = async () => {
    try {
      const [{ data: list }, { data: cnt }] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setItems(list);
      setUnread(cnt.count || 0);
    } catch (_e) { /* silent -- notifications are non-critical */ }
  };

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 30000);
    return () => clearInterval(iv);
  }, []);

  const markAll = async () => {
    // Phase 14A -- new bulk endpoint. Keeps legacy /read-all as fallback so
    // old bundles served from cache do not error.
    try {
      await api.post("/notifications/mark-all-read");
    } catch (_e) {
      try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
    }
    refresh();
  };

  // D54-f Group F6 -- migrated `user?.role === "FRONT_DESK"` to
  // `front_desk_persona_ui` matrix key. Persona UX hint only; server
  // routes still enforce their own gates.
  const isFde = useMatrix("front_desk_persona_ui");
  const sortedItems = useMemo(() => {
    if (!isFde) return items;
    const stock = items.filter(isLowStockNotif);
    const rest = items.filter((n) => !isLowStockNotif(n));
    return [...stock, ...rest];
  }, [items, isFde]);

  const handleClick = async (n) => {
    try {
      if (!n.read) await api.post(`/notifications/${n.id}/read`);
    } catch (_e) { /* ignore */ }
    refresh();
    if (n.link) navigate(n.link);
  };

  // D55d-uat3 -- inline approve/reject actions on access-request
  // notifications. The notification's `data.request_id` or the id
  // param in the `link` URL is the target. Uses the same decision
  // endpoints as the Inbox tab.
  const extractRequestId = (n) => {
    if (n?.data?.request_id) return n.data.request_id;
    if (typeof n?.link === "string") {
      try {
        const u = new URL(n.link, window.location.origin);
        return u.searchParams.get("id") || null;
      } catch (_e) { return null; }
    }
    return null;
  };
  const decide = async (n, kind, extra = {}) => {
    const rid = extractRequestId(n);
    if (!rid) { toast.error("Missing request id"); return; }
    try {
      const body = kind === "reject"
        ? { notes: "quick-reject from notification bell" }
        : { approve_as: extra.approve_as || "permanent",
            duration_hours: extra.duration_hours || null,
            notes: "quick-approve from notification bell" };
      await api.post(`/access-requests/${rid}/${kind}`, body);
      // Mark the notification read to remove it from the queue.
      try { if (!n.read) await api.post(`/notifications/${n.id}/read`); } catch (_e) { /* silent */ }
      toast.success(kind === "reject" ? "Rejected" : "Approved");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${kind} failed`);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white/5 border border-[var(--surface-border)] hover:bg-white/10"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-gradient-primary text-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[340px] bg-popover/95 backdrop-blur-xl border-[var(--surface-border)]"
        data-testid="notifications-panel"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--surface-border)]">
          <div className="font-heading text-xs tracking-widest text-foreground/80">{t("common.notifications")}</div>
          <div className="flex items-center gap-2">
            <Link
              to="/me/notifications"
              data-testid="notif-settings-link"
              aria-label="Notification settings"
              className="text-foreground/50 hover:text-foreground inline-flex items-center"
            >
              <Settings className="h-3.5 w-3.5" />
            </Link>
            <button
              onClick={markAll}
              disabled={unread === 0}
              data-testid="mark-all-read-btn"
              className="text-[11px] uppercase tracking-widest text-sky disabled:opacity-40"
            >
              {t("common.markAllRead")}
            </button>
          </div>
        </div>
        <div className="max-h-[320px] overflow-y-auto">
          {sortedItems.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
              {t("common.noNotifications")}
            </div>
          )}
          {sortedItems.map((n) => {
            const lowStock = isLowStockNotif(n);
            const isAccessReq = n.type === "access_request_new";
            const rowTestId = lowStock ? `notification-lowstock-${n.id}` : `notification-${n.id}`;
            return (
              <div
                key={n.id}
                data-testid={rowTestId}
                className={[
                  "w-full text-left px-3 py-2.5 border-b border-[var(--surface-border)]/60 hover:bg-white/5 transition-colors",
                  n.read ? "opacity-70" : "bg-white/5",
                  lowStock ? "relative" : "",
                ].join(" ")}
              >
                {lowStock && (
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-0 h-full w-1 bg-rose-500"
                  />
                )}
                <button
                  type="button"
                  onClick={() => handleClick(n)}
                  className="w-full text-left"
                  data-testid={`notification-body-${n.id}`}
                >
                  <div className="flex items-center gap-2">
                    {lowStock && (
                      <span
                        data-testid={`notification-stock-pill-${n.id}`}
                        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-200 border border-rose-500/40"
                      >
                        <PackageX className="h-2.5 w-2.5" /> Stock
                      </span>
                    )}
                    <div className="text-sm font-medium flex-1 truncate">{n.title}</div>
                    {lowStock && !n.read && (
                      <span
                        data-testid={`notification-lowstock-dot-${n.id}`}
                        className="h-2 w-2 rounded-full bg-rose-500 shrink-0"
                      />
                    )}
                  </div>
                  {n.body && <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>}
                  <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-widest">
                    {formatIST(n.created_at)}
                  </div>
                </button>
                {isAccessReq && !n.read && (
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`ar-notif-actions-${n.id}`}>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); decide(n, "approve", { approve_as: "permanent" }); }}
                      data-testid={`ar-notif-approve-${n.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] uppercase tracking-widest hover:bg-emerald-500/30"
                    >
                      <Check className="h-3 w-3" /> Approve
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); decide(n, "approve", { approve_as: "temporary", duration_hours: 2 }); }}
                      data-testid={`ar-notif-approve-2h-${n.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-sky-500/20 text-sky-200 border border-sky-500/40 text-[10px] uppercase tracking-widest hover:bg-sky-500/30"
                    >
                      <Clock className="h-3 w-3" /> 2h
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); decide(n, "approve", { approve_as: "temporary", duration_hours: 24 }); }}
                      data-testid={`ar-notif-approve-24h-${n.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-sky-500/20 text-sky-200 border border-sky-500/40 text-[10px] uppercase tracking-widest hover:bg-sky-500/30"
                    >
                      <Clock className="h-3 w-3" /> 24h
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); decide(n, "reject"); }}
                      data-testid={`ar-notif-reject-${n.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-500/20 text-rose-200 border border-rose-500/40 text-[10px] uppercase tracking-widest hover:bg-rose-500/30"
                    >
                      <X className="h-3 w-3" /> Reject
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleClick(n); }}
                      data-testid={`ar-notif-open-${n.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 text-foreground/80 border border-white/10 text-[10px] uppercase tracking-widest hover:bg-white/10"
                    >
                      Open
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default NotificationsBell;
